import React, { useState, useEffect } from 'react';
import { Vessel, ImportRecord, BitumenGrade } from '../../types';
import { X, Save, Plus, Trash2, Ship, Calendar, AlertCircle } from 'lucide-react';
import { generateId } from '../../services/storageService';
import { FINAL_DESTINATIONS } from './SmartEntry';

interface ManifestEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  vessel: Vessel | null;
  onSave: (updatedVessel: Vessel) => void;
}

export const ManifestEditModal: React.FC<ManifestEditModalProps> = ({ isOpen, onClose, vessel, onSave }) => {
  const [editedVessel, setEditedVessel] = useState<Vessel | null>(null);

  useEffect(() => {
    if (isOpen && vessel) {
      setEditedVessel(JSON.parse(JSON.stringify(vessel))); // Deep clone
    }
  }, [isOpen, vessel]);

  if (!isOpen || !editedVessel) return null;

  const handleUpdateManifest = (index: number, field: keyof ImportRecord, value: any) => {
    const next = { ...editedVessel };
    next.manifest[index] = { ...next.manifest[index], [field]: value };
    setEditedVessel(next);
  };

  const handleAddRow = () => {
    const next = { ...editedVessel };
    next.manifest.push({
      id: generateId(),
      vesselName: editedVessel.name,
      importerName: '',
      supplier: '',
      notifyParty: '',
      bitumenGrade: BitumenGrade.G_60_70,
      quantity: 0,
      arrivalDate: editedVessel.arrivalDate,
      finalDestination: FINAL_DESTINATIONS[0] // Default to Tanzania
    });
    setEditedVessel(next);
  };

  const handleRemoveRow = (index: number) => {
    const next = { ...editedVessel };
    next.manifest.splice(index, 1);
    setEditedVessel(next);
  };

  const handleSave = () => {
    onSave(editedVessel);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-7xl overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
        <header className="p-8 bg-slate-900 text-white flex justify-between items-center shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Ship size={24} />
            </div>
            <div>
              <h3 className="text-xl font-black uppercase tracking-tight">Edit Port Record</h3>
              <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">Correction Mode: {editedVessel.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
            <X size={24} />
          </button>
        </header>

        <div className="p-8 overflow-y-auto custom-scrollbar flex-grow space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block px-1">Vessel Name</label>
              <input 
                type="text" 
                value={editedVessel.name} 
                onChange={e => setEditedVessel({...editedVessel, name: e.target.value.toUpperCase()})}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold uppercase outline-none focus:ring-4 focus:ring-indigo-500/10"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block px-1">IMO Number</label>
              <input 
                type="text" 
                value={editedVessel.imo || ''} 
                onChange={e => setEditedVessel({...editedVessel, imo: e.target.value})}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-mono outline-none focus:ring-4 focus:ring-indigo-500/10"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block px-1">Arrival Date</label>
              <div className="relative">
                <Calendar size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input 
                  type="date" 
                  value={editedVessel.arrivalDate} 
                  onChange={e => setEditedVessel({...editedVessel, arrivalDate: e.target.value})}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:ring-4 focus:ring-indigo-500/10"
                />
              </div>
            </div>
          </div>

          <div className="rounded-[32px] border border-slate-100 overflow-hidden shadow-inner bg-slate-50">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-100/50 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                <tr>
                  <th className="px-6 py-4 text-left">Importer</th>
                  <th className="px-6 py-4 text-left">Notify Party</th>
                  <th className="px-6 py-4 text-left">Supplier</th>
                  <th className="px-6 py-4 text-left">Grade</th>
                  <th className="px-6 py-4 text-right">Qty (MT)</th>
                  <th className="px-6 py-4 text-left">Destination</th>
                  <th className="px-6 py-4 text-left">BL Number</th>
                  <th className="px-6 py-4 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {editedVessel.manifest.map((item, idx) => (
                  <tr key={idx} className="group hover:bg-slate-50/50">
                    <td className="px-6 py-3">
                      <input 
                        type="text" 
                        value={item.importerName} 
                        onChange={e => handleUpdateManifest(idx, 'importerName', e.target.value.toUpperCase())}
                        className="w-full bg-transparent border-b border-transparent focus:border-indigo-400 outline-none text-sm font-bold text-slate-800"
                      />
                    </td>
                    <td className="px-6 py-3">
                      <input 
                        type="text" 
                        value={item.notifyParty || ''} 
                        onChange={e => handleUpdateManifest(idx, 'notifyParty', e.target.value.toUpperCase())}
                        className="w-full bg-transparent border-b border-transparent focus:border-indigo-400 outline-none text-xs text-slate-500 italic"
                        placeholder="Notify party"
                      />
                    </td>
                    <td className="px-6 py-3">
                      <input 
                        type="text" 
                        value={item.supplier} 
                        onChange={e => handleUpdateManifest(idx, 'supplier', e.target.value.toUpperCase())}
                        className="w-full bg-transparent border-b border-transparent focus:border-indigo-400 outline-none text-sm text-slate-600"
                      />
                    </td>
                    <td className="px-6 py-3">
                      <select 
                        value={item.bitumenGrade} 
                        onChange={e => handleUpdateManifest(idx, 'bitumenGrade', e.target.value)}
                        className="bg-indigo-50 text-indigo-700 rounded-lg px-2 py-1 text-[10px] font-black uppercase outline-none"
                      >
                        {Object.values(BitumenGrade).map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <input 
                        type="number" 
                        value={item.quantity} 
                        onChange={e => handleUpdateManifest(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-24 bg-transparent border-b border-transparent focus:border-indigo-400 outline-none text-sm text-right font-black"
                      />
                    </td>
                    <td className="px-6 py-3">
                      <select 
                        value={item.finalDestination} 
                        onChange={e => handleUpdateManifest(idx, 'finalDestination', e.target.value)}
                        className="bg-slate-50 border border-slate-100 rounded-lg px-2 py-1 text-[10px] font-black text-slate-600 uppercase outline-none appearance-none cursor-pointer"
                      >
                         {FINAL_DESTINATIONS.map(d => (
                            <option key={d} value={d}>{d}</option>
                         ))}
                      </select>
                    </td>
                    <td className="px-6 py-3">
                      <input 
                        type="text" 
                        value={item.blNumber || ''} 
                        onChange={e => handleUpdateManifest(idx, 'blNumber', e.target.value.toUpperCase())}
                        className="w-full bg-transparent border-b border-transparent focus:border-indigo-400 outline-none text-xs font-mono text-slate-400"
                      />
                    </td>
                    <td className="px-6 py-3">
                      <button 
                        onClick={() => handleRemoveRow(idx)}
                        className="p-1 text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button 
              onClick={handleAddRow}
              className="w-full py-4 text-center text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-indigo-600 hover:bg-slate-100 transition-all flex items-center justify-center gap-2 border-t border-slate-100"
            >
              <Plus size={14} /> Append New Manifest Line
            </button>
          </div>
        </div>

        <footer className="p-8 bg-slate-50 border-t border-slate-100 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-2 text-rose-500 bg-rose-50 px-4 py-2 rounded-xl border border-rose-100">
             <AlertCircle size={14} />
             <span className="text-[10px] font-black uppercase tracking-widest">Manual correction requires verification</span>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={onClose}
              className="px-8 py-3 text-slate-500 font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 rounded-2xl transition-all"
            >
              Discard
            </button>
            <button 
              onClick={handleSave}
              className="px-12 py-3 bg-slate-900 text-white font-black text-[10px] uppercase tracking-[0.2em] rounded-2xl shadow-xl hover:bg-slate-800 transition-all active:scale-95 flex items-center gap-3"
            >
              <Save size={16} /> Save Changes
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};
